/*!
  Pioneer WFRIA EmptyState v4.0.0-beta.53
  © 2023 Wells Fargo. All rights reserved.
*/
try {
  if (!window.__PIONEER_GLOBAL__) {
    window.__PIONEER_GLOBAL__ = {
      version: "4.0.0-beta.53",
      components: ["EmptyState"]
    };
  } else if (window.__PIONEER_GLOBAL__.components && window.__PIONEER_GLOBAL__.components.indexOf("EmptyState") < 0) {
    window.__PIONEER_GLOBAL__.components.push("EmptyState");
  }
} catch (error) {
  // catching server side rendering issues - windows is not defined there!
}

import _defineProperty from "@babel/runtime/helpers/defineProperty";
import _objectWithoutProperties from "@babel/runtime/helpers/objectWithoutProperties";
const _excluded = ["actionElement", "alignContent", "header", "message", "messageId", "noIllustration"];

function ownKeys(object, enumerableOnly) { var keys = Object.keys(object); if (Object.getOwnPropertySymbols) { var symbols = Object.getOwnPropertySymbols(object); enumerableOnly && (symbols = symbols.filter(function (sym) { return Object.getOwnPropertyDescriptor(object, sym).enumerable; })), keys.push.apply(keys, symbols); } return keys; }

function _objectSpread(target) { for (var i = 1; i < arguments.length; i++) { var source = null != arguments[i] ? arguments[i] : {}; i % 2 ? ownKeys(Object(source), !0).forEach(function (key) { _defineProperty(target, key, source[key]); }) : Object.getOwnPropertyDescriptors ? Object.defineProperties(target, Object.getOwnPropertyDescriptors(source)) : ownKeys(Object(source)).forEach(function (key) { Object.defineProperty(target, key, Object.getOwnPropertyDescriptor(source, key)); }); } return target; }

import { useTheme } from "@emotion/react";
import PropTypes from "prop-types";
import React from "react";
import { invalidIf } from "../common/proptypes";
import extractTheme from "./extractTheme";
import { jsx as _jsx } from "@emotion/react/jsx-runtime";
import { jsxs as _jsxs } from "@emotion/react/jsx-runtime";
const BUTTON = process.env.NODE_ENV !== "production" ? "button" : {};

function Header(_ref) {
  let {
    content,
    cssHeader
  } = _ref;

  if (typeof content === "string") {
    return _jsx("div", {
      css: cssHeader,
      children: _jsx("h2", {
        children: content
      })
    });
  } else {
    return _jsx("div", {
      css: cssHeader,
      children: content
    });
  }
}
/**
 * EmptyState is a component that consists of an illustration, heading, and message
 * to communicate empty data. It communicates to the user that there’s no content to
 * display and briefly explains why.  An empty state can also suggest the user
 * on what to do next through the inclusion of a Button or Anchor.
 */


function EmptyState(_ref2) {
  let {
    actionElement,
    alignContent,
    header,
    message,
    messageId,
    noIllustration
  } = _ref2,
      otherProps = _objectWithoutProperties(_ref2, _excluded);

  const theme = useTheme();
  const {
    cssRoot,
    cssHeader,
    cssMessage,
    cssIllustration
  } = extractTheme({
    theme,
    alignContent
  });
  return _jsxs("div", _objectSpread(_objectSpread({
    css: cssRoot
  }, otherProps), {}, {
    children: [!noIllustration && _jsx("div", {
      "aria-hidden": "true",
      css: cssIllustration,
      "data-testid": "emptystate-illustration"
    }), _jsx(Header, {
      cssHeader: cssHeader,
      content: header
    }), _jsx("div", {
      css: cssMessage,
      id: messageId,
      children: message
    }), actionElement]
  }));
}

EmptyState.propTypes = process.env.NODE_ENV !== "production" ? {
  /**
   * Message text.
   */
  message: PropTypes.string.isRequired,

  /**
   * Button or Anchor call to action.
   */
  actionElement: invalidIf(PropTypes.node, (props, propName) => props[propName] && props[propName].props.type !== BUTTON && !props[propName].props.href, "when `actionElement` prop is a node other than Button or Anchor."),

  /**
   * Aligment of EmptyState contents.
   */
  alignContent: PropTypes.oneOf(["left", "center"]),

  /**
   * Header text with default h2 or header text with heading tag.
   */
  header: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),

  /**
   * Id for message text.
   */
  messageId: PropTypes.string,

  /**
   * If true, the illustration won't be present.
   */
  noIllustration: PropTypes.bool
} : {};
EmptyState.defaultProps = {
  actionElement: null,
  alignContent: "left",
  header: null,
  messageId: null,
  noIllustration: false
};
export default EmptyState;